import * as React from 'react';
import moment from 'moment-timezone';

import {
  CheckboxVisibility,
  ConstrainMode,
  ContextualMenu,
  DetailsList,
  HoverCard,
  HoverCardType,
  IColumn,
  IContextualMenuProps,
  IconButton,
  IContextualMenuItem,
} from '@fluentui/react';
import { Claim, State } from '../../redux/claim/types';

import { ROUTES } from '../../config/routes';

export interface ClaimDetailsListProps {
  items: Claim[];
  onClaimActionsRequested: () => IContextualMenuItem[];
  onActiveItemChanged: (item: Claim, index?: number) => void;
}

export interface ClaimDetailsListState {
  sortedItems?: Claim[];
  columns?: IColumn[];
}

export class ClaimDetailsList extends React.Component<ClaimDetailsListProps, ClaimDetailsListState> {
  constructor(props: ClaimDetailsListProps) {
    super(props);

    this.state = {
      columns: this.buildColumns(),
      sortedItems: [],
    };
  }

  public componentDidMount() {
    this.setItems(this.props.items);
  }

  public componentDidUpdate(prevProps: ClaimDetailsListProps) {
    if (this.props.items !== prevProps.items) {
      this.setItems(this.props.items);
    }
  }

  public render(): JSX.Element {
    const { sortedItems, columns } = this.state;

    return (
      <DetailsList
        compact={true}
        className="details-list-wrapper"
        constrainMode={ConstrainMode.horizontalConstrained}
        checkboxVisibility={CheckboxVisibility.hidden}
        items={sortedItems as any[]}
        setKey="set"
        columns={columns}
        onActiveItemChanged={this.onActiveItemChanged}
      />
    );
  }

  private setItems(claims: Claim[]) {
    this.setState({ sortedItems: claims });
  }

  private onActiveItemChanged = (item: Claim, index?: number) => {
    this.props.onActiveItemChanged(item, index);
  };

  private getMenu(menuProps: IContextualMenuProps): JSX.Element {
    const items = this.props.onClaimActionsRequested();
    const copiedMenuProps = { ...menuProps, items: items };
    return <ContextualMenu {...copiedMenuProps} />;
  }

  private renderContextualItemDropdown() {
    return (
      <IconButton
        className="hidable-menu-button"
        allowDisabledFocus={true}
        iconProps={{ iconName: 'contextMenu' }}
        menuAs={(props) => this.getMenu(props)}
        menuProps={{
          items: [],
        }}
      />
    );
  }

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'externalReferenceId',
        name: 'Reference Id',
        fieldName: 'externalReferenceId',
        minWidth: 120,
        maxWidth: 160,
        isResizable: true,
        onRender: (item: Claim) => {
          return (
            <>
              <a rel="noopener noreferrer" href={`${ROUTES.claims}/${item.id}`}>
                {item.externalReferenceId}
              </a>
              <span style={{ marginLeft: 6 }}>{item.revision > 1 ? `(rev. ${item.revision})` : ''}</span>
            </>
          );
        },
      },
      {
        key: 'state',
        name: 'State',
        fieldName: 'state',
        minWidth: 60,
        maxWidth: 100,
        onRender: (item: Claim) => {
          return <ClaimStateView claim={item} />;
        },
      },
      {
        key: 'payer',
        name: 'Payer',
        fieldName: '',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: Claim) => {
          return item.payer.name;
        },
      },
      {
        key: 'location',
        name: 'Location',
        fieldName: '',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: Claim) => {
          return item.placeOfServiceAddress?.state || '';
        },
      },
      {
        key: 'patientName',
        name: 'Patient Name',
        fieldName: '',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: Claim) => {
          const patient = item.dependent || item.subscriber;
          return `${patient.firstName} ${patient.lastName}`;
        },
      },
      {
        key: 'subscriberName',
        name: 'Subscriber Name',
        fieldName: '',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: Claim) => {
          return `${item.subscriber.firstName} ${item.subscriber.lastName}`;
        },
      },
      {
        key: 'paidAmount',
        name: 'Paid Amount',
        fieldName: 'payerPaid',
        minWidth: 70,
        maxWidth: 70,
        onRender: (item: Claim) => {
          return <div style={{ textAlign: 'right' }}>{item.payerPaid ? `$${item.payerPaid}` : ''}</div>;
        },
      },
      {
        key: 'amountPatientPaid',
        name: 'Patient Paid',
        fieldName: 'amountPatientPaid',
        minWidth: 70,
        maxWidth: 70,
        onRender: (item: Claim) => {
          return <div style={{ textAlign: 'right' }}>{item.amountPatientPaid ? `$${item.amountPatientPaid}` : ''}</div>;
        },
      },
      {
        key: 'dateOfService',
        name: 'Date of Service',
        fieldName: 'dateOfService',
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: Claim) => {
          if (item.dateOfService) {
            const date = moment(item.dateOfService);
            return date.format('M/D/YYYY');
          }
          return null;
        },
      },
      {
        key: 'submittedDate',
        name: 'Submitted At',
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: Claim) => {
          if (item.submittedDate) {
            const date = moment(item.submittedDate);
            return date.format('M/D/YYYY');
          }
          return null;
        },
      },
      {
        key: 'options',
        name: ' ',
        minWidth: 30,
        maxWidth: 30,
        onRender: () => {
          return this.renderContextualItemDropdown();
        },
      },
    ];
  }
}

interface ClaimStateViewProps {
  claim: Claim;
}

class ClaimStateView extends React.Component<ClaimStateViewProps> {
  public render(): JSX.Element {
    const { state } = this.props.claim;

    const plainCardProps = {
      onRenderPlainCard: (props: Claim) => {
        return (
          <div style={{ margin: 10 }}>
            {(props.validationErrors || []).map((e, i) => (
              <div key={i}>{e}</div>
            ))}
          </div>
        );
      },
      renderData: this.props.claim,
    };

    switch (state) {
      case State.Rejected:
      case State.ValidationFailed:
        return (
          <HoverCard key={this.props.claim.externalReferenceId} plainCardProps={plainCardProps} type={HoverCardType.plain}>
            <div className="flex">
              <i className={`ms-font-m ms-Icon ms-Icon--StatusErrorFull`} style={{ color: 'red' }} />
              <span style={{ marginLeft: 4 }}>{state}</span>
            </div>
          </HoverCard>
        );
      case State.Finalized:
        return (
          <div className="flex">
            <i className={`ms-font-m ms-Icon ms-Icon--CheckMark`} style={{ color: 'green' }} />
            <span style={{ marginLeft: 4 }}>{state}</span>
          </div>
        );

      default:
        return <span>{state}</span>;
    }
  }
}
